"use client";

import React, { createContext, useContext, useRef, ReactNode } from 'react';

interface NavbarContextType {
    companyRef: React.RefObject<HTMLDivElement>;
    productsRef: React.RefObject<HTMLDivElement>;
    blogRef: React.RefObject<HTMLDivElement>;
    contactusRef: React.RefObject<HTMLDivElement>;
    scrollToSection: (ref: React.RefObject<HTMLDivElement>) => void;
}

const NavbarContext = createContext<NavbarContextType | undefined>(undefined);

export function NavbarProvider({ children }: { children: ReactNode }) {
    const companyRef = useRef<HTMLDivElement>(null);
    const productsRef = useRef<HTMLDivElement>(null);
    const blogRef = useRef<HTMLDivElement>(null);
    const contactusRef = useRef<HTMLDivElement>(null);

    const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        let retryCount = 0;
        const maxRetries = 10; // Maximum 10 retries (500ms total)
        
        // Function to perform the scroll
        const performScroll = () => {
            if (!ref.current) {
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(performScroll, 50);
                }
                return;
            }
            
            // Verify element is in the DOM
            if (!document.contains(ref.current)) {
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(performScroll, 50);
                }
                return;
            }
            
            // Get navbar height dynamically
            const navbar = document.querySelector('nav') || document.querySelector('[role="navigation"]');
            const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
            
            // Get element position relative to document
            const elementRect = ref.current.getBoundingClientRect();
            const elementTop = elementRect.top + window.pageYOffset;
            
            // Calculate scroll position accounting for navbar
            const offsetPosition = elementTop - navbarHeight - 20; // 20px extra padding
            
            // Scroll to calculated position
            window.scrollTo({
                top: Math.max(0, offsetPosition), // Ensure we don't scroll to negative position
                behavior: 'smooth'
            });
        };
        
        // Use double requestAnimationFrame to ensure DOM is fully rendered
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                performScroll();
            });
        });
    };

    return (
        <NavbarContext.Provider value={{ companyRef, productsRef, blogRef, contactusRef, scrollToSection }}>
            {children}
        </NavbarContext.Provider>
    );
}

export function useNavbar() {
    const context = useContext(NavbarContext);
    if (context === undefined) {
        throw new Error('useNavbar must be used within a NavbarProvider');
    }
    return context;
}

